import { mutationOptions, queryOptions } from '@tanstack/react-query';

import { API_MODULES } from '@lib/api/constants';
import DIContainer from '@lib/dicontainer';
import {
  OrderUpdateInput,
  PurchaseOrderDetails,
  SalesOrderDetails,
  SellerCancellationInput,
} from '@lib/types';
import { AdminPageFilters } from '@lib/types/admin-page';

import { MutationVariables } from './types';

const usecase = DIContainer.getOrdersUseCase();
const PENDING_SHIPMENTS_STALE_TIME_MS = 30_000;
const PENDING_SHIPMENTS_REFRESH_INTERVAL_MS = 60_000;

export const ordersKey = [API_MODULES.ORDERS];

export const orderDetailsOptions = (
  id: string,
  perspective: 'admin' | 'purchase' | 'sale',
) =>
  queryOptions<PurchaseOrderDetails | SalesOrderDetails>({
    queryKey: [...ordersKey, perspective, id],
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchIntervalInBackground: false,
    refetchInterval: (query) =>
      query.state.data?.status === 'pending_payment' ? 15_000 : false,
    queryFn: async ({
      signal,
    }): Promise<PurchaseOrderDetails | SalesOrderDetails> =>
      perspective === 'admin'
        ? usecase.findAdmin(id, signal)
        : perspective === 'purchase'
          ? usecase.findPurchase(id, signal)
          : usecase.findSale(id, signal),
  });

export const orderCommentsOptions = (id: string) =>
  queryOptions({
    queryKey: [...ordersKey, id, 'comments'],
    queryFn: ({ signal }) => usecase.listComments(id, signal),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

export const purchasesOptions = () =>
  queryOptions({
    queryKey: [...ordersKey, 'purchases'],
    queryFn: ({ signal }) => usecase.listPurchases(signal),
  });

export const salesOptions = () =>
  queryOptions({
    queryKey: [...ordersKey, 'sales'],
    queryFn: ({ signal }) => usecase.listSales(signal),
  });

export const pendingShipmentsOptions = () =>
  queryOptions({
    queryKey: [...ordersKey, 'sales', 'pending'],
    queryFn: ({ signal }) => usecase.countPendingShipments(signal),
    staleTime: PENDING_SHIPMENTS_STALE_TIME_MS,
    refetchInterval: PENDING_SHIPMENTS_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

export const adminOrderStatisticsOptions = (year: number) =>
  queryOptions({
    queryKey: [...ordersKey, 'admin-statistics', year],
    queryFn: ({ signal }) => usecase.getAdminStatistics(year, signal),
  });

export const adminOrdersOptions = () =>
  queryOptions({
    queryKey: [...ordersKey, 'admin'],
    queryFn: ({ signal }) => usecase.listAdmin(signal),
  });

export const disputeMetricsOptions = () =>
  queryOptions({
    queryKey: [...ordersKey, 'dispute-metrics'],
    queryFn: ({ signal }) => usecase.getDisputeMetrics(signal),
  });

export const orderNotificationsOptions = () =>
  queryOptions({
    queryKey: [...ordersKey, 'notifications'],
    queryFn: ({ signal }) => usecase.listNotifications(signal),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

export const markAllOrderNotificationsReadOptions = () =>
  mutationOptions({
    mutationFn: () => usecase.markAllNotificationsRead(),
  });

export const markOrderNotificationReadOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => usecase.markNotificationRead(id),
  });

export const updateOrderOptions = () =>
  mutationOptions({
    mutationFn: ({ id, input }: { id: string; input: OrderUpdateInput }) =>
      usecase.update(id, input),
  });

export const updateAdminOrderOptions = () =>
  mutationOptions({
    mutationFn: ({ id, input }: { id: string; input: OrderUpdateInput }) =>
      usecase.updateAdmin(id, input),
  });

export const indicateOrderDeliveryOptions = () =>
  mutationOptions({
    mutationFn: ({
      params,
      payload,
    }: MutationVariables<{ id: string }, { file: File }>) =>
      usecase.indicateDelivery(params.id, payload.file),
  });

export const downloadDeliveryProofOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => usecase.downloadDeliveryProof(id),
  });

export const confirmOrderReceivedOptions = () =>
  mutationOptions({ mutationFn: (id: string) => usecase.confirmReceived(id) });

export const justifyNonShipmentOptions = () =>
  mutationOptions({
    mutationFn: ({
      id,
      justification,
    }: {
      id: string;
      justification: string;
    }) => usecase.justifyNonShipment(id, justification),
  });

export const openOrderDisputeOptions = () =>
  mutationOptions({
    mutationFn: ({
      id,
      reason,
      description,
      requestedOutcome,
    }: {
      id: string;
      reason: string;
      description: string;
      requestedOutcome: 'refund' | 'fulfillment';
    }) => usecase.openDispute(id, reason, description, requestedOutcome),
  });

export const respondToDisputeOptions = () =>
  mutationOptions({
    mutationFn: ({
      id,
      response,
      proposal,
    }: {
      id: string;
      response: string;
      proposal?: string;
    }) => usecase.respondToDispute(id, response, proposal),
  });

export const decideDisputeProposalOptions = () =>
  mutationOptions({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      usecase.decideDisputeProposal(id, accept),
  });

export const claimDisputeOptions = () =>
  mutationOptions({ mutationFn: (id: string) => usecase.claimDispute(id) });

export const addDisputeInternalNoteOptions = () =>
  mutationOptions({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      usecase.addDisputeInternalNote(id, note),
  });

export const requestDisputeInfoOptions = () =>
  mutationOptions({
    mutationFn: ({
      id,
      target,
      note,
    }: {
      id: string;
      target: 'buyer' | 'seller';
      note: string;
    }) => usecase.requestDisputeInfo(id, target, note),
  });

export const resolveDisputeOptions = () =>
  mutationOptions({
    mutationFn: ({
      id,
      resolution,
      note,
    }: {
      id: string;
      resolution: 'buyer' | 'seller' | 'return';
      note: string;
    }) => usecase.resolveDispute(id, resolution, note),
  });

export const markReturnShippedOptions = () =>
  mutationOptions({
    mutationFn: ({ id, trackingCode }: { id: string; trackingCode: string }) =>
      usecase.markReturnShipped(id, trackingCode),
  });

export const retryRefundOptions = () =>
  mutationOptions({ mutationFn: (id: string) => usecase.retryRefund(id) });

export const confirmPhysicalReturnOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => usecase.confirmPhysicalReturn(id),
  });

export const orderEvidenceOptions = (id: string) =>
  queryOptions({
    queryKey: [...ordersKey, id, 'evidence'],
    queryFn: ({ signal }) => usecase.listDisputeEvidence(id, signal),
  });

export const uploadOrderEvidenceOptions = () =>
  mutationOptions({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      usecase.uploadDisputeEvidence(id, file),
  });

export const downloadOrderEvidenceOptions = () =>
  mutationOptions({
    mutationFn: ({ id, evidenceId }: { id: string; evidenceId: string }) =>
      usecase.downloadDisputeEvidence(id, evidenceId),
  });

export const addOrderCommentOptions = () =>
  mutationOptions({
    mutationFn: ({
      id,
      message,
      image,
    }: {
      id: string;
      message: string;
      image?: File;
    }) => usecase.addComment(id, message, image),
  });

export const downloadCommentImageOptions = () =>
  mutationOptions({
    mutationFn: ({ id, commentId }: { id: string; commentId: string }) =>
      usecase.downloadCommentImage(id, commentId),
  });

export const createStoreReviewOptions = () =>
  mutationOptions({
    mutationFn: ({
      id,
      rating,
      comment,
    }: {
      id: string;
      rating: number;
      comment: string;
    }) => usecase.createReview(id, rating, comment),
  });

export const simulateSandboxPaymentOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => usecase.simulateSandboxPayment(id),
  });

export const adminOrdersPageOptions = (filters: AdminPageFilters) =>
  queryOptions({
    queryKey: [...ordersKey, 'admin-page', filters],
    queryFn: ({ signal }) => usecase.listAdminPage(filters, signal),
  });

export const cancelSellerOrderOptions = () =>
  mutationOptions({
    mutationFn: ({
      params,
      payload,
    }: MutationVariables<{ id: string }, SellerCancellationInput>) =>
      usecase.cancelBySeller(params.id, payload),
  });
